import {
  attr,
  htmlTree,
  select,
  space,
  textOf,
  presented,
  subOne,
  type HNode,
} from "./html";
import { Graph, urlValue } from "./jsonld";
import { compareDates, compareOrder, dateValue } from "./normalize";
import { validateProfile } from "./profile";
import {
  ControlError,
  fail,
  arr,
  stable,
  sha256,
  VERSION,
  LIMITS,
  type Profile,
  type Input,
  type Rule,
  type Finding,
  type Evidence,
  type PageReport,
  type Report,
  type Status,
} from "./types";
const LIMIT =
  "Concordance de valeurs dans le fichier fourni ; visibilité et exactitude éditoriale non établies.";
const NEXT =
  "Vérifier le champ source, le gabarit et la version du cache ; conserver une preuve éditoriale indépendante.";
function finding(rule: Rule, id = rule.id): Finding {
  return {
    rule_id: id,
    label: rule.label,
    expected: rule.expected,
    status: "non_determine",
    reason: "Contrôle non exécuté.",
    selector: `${rule.scope} → ${rule.selector}`,
    matchCount: 0,
    htmlExcerpt: [],
    htmlRaw: null,
    jsonRaw: null,
    htmlNormalized: null,
    jsonNormalized: null,
    provenance: [],
    comparison: stable(rule.policy),
    limit: LIMIT,
    nextCheck: NEXT,
  };
}
function issue(id: string, status: Status, reason: string): Finding {
  return {
    ...finding({
      id,
      label: id,
      expected: true,
      scope: "document",
      selector: 'script[type="application/ld+json"]',
      policy: {},
    } as Rule),
    status,
    reason,
  };
}
function requireString(v: any, label: string): string {
  if (typeof v !== "string")
    fail("non_pris_en_charge", `${label} doit être un texte unique.`);
  if (!v.trim()) fail("non_determine", `${label} est vide.`);
  return v;
}
function assertUnique(values: string[], label: string) {
  if (new Set(values).size !== values.length)
    fail("non_determine", `${label} : clés dupliquées, appariement ambigu.`);
}
function compare(f: Finding, a: any, b: any) {
  f.htmlNormalized = a;
  f.jsonNormalized = b;
  const same = stable(a) === stable(b);
  f.status = same ? "concordant" : "ecart";
  f.reason = same
    ? "Valeurs concordantes selon la règle déclarée."
    : "Les valeurs extraites diffèrent selon la règle déclarée.";
}
function readLinks(n: HNode, selector: string, base?: string): string | null {
  const nodes = select(n, selector);
  if (nodes.length > 1)
    fail("non_determine", "Plusieurs destinations pour un élément.");
  if (!nodes.length) return null;
  const h = attr(nodes[0], "href");
  if (h === undefined || h === "")
    fail("non_determine", "Élément lien présent sans destination exploitable.");
  return urlValue(h, base);
}
function htmlFAQAnswer(n: HNode, links: boolean, base?: string) {
  const text = space(textOf(n));
  return links
    ? {
        text,
        links: select(n, "a[href]").map((a) => urlValue(attr(a, "href"), base)),
      }
    : text;
}
export async function analyzePage(
  input: Input,
  profile: Profile,
  profileSha256?: string,
): Promise<PageReport> {
  const p = validateProfile(profile);
  const hash = profileSha256 || (await sha256(stable(p)));
  const out: PageReport = {
    file: input.name,
    inputMode: input.mode,
    referenceUrl: input.referenceUrl,
    capturedAt: input.capturedAt,
    provenance: input.provenance,
    inputSha256: await sha256(input.html),
    profile: { id: p.id, version: p.version, sha256: hash },
    findings: [],
    concordant: false,
    partial: false,
    scripts: 0,
  };
  try {
    if (!["html_source", "dom_snapshot"].includes(input.mode))
      fail("invalide", "Mode d’entrée inconnu.");
    if (!input.provenance || !input.name)
      fail("invalide", "Nom et provenance nécessaires.");
    if (input.capturedAt && dateValue(input.capturedAt).precision !== "instant")
      fail(
        "invalide",
        "La capture déclarée nécessite un horodatage ISO avec fuseau.",
      );
    const root = htmlTree(input.html);
    if (!select(root, p.marker).length)
      fail("non_determine", `Hors profil : marqueur ${p.marker} non trouvé.`);
    let declared = input.referenceUrl
      ? urlValue(input.referenceUrl)
      : undefined;
    if (p.pageUrl) {
      const found = select(root, p.pageUrl.selector);
      if (found.length !== 1)
        fail(
          "non_determine",
          `${found.length} URL de page sélectionnées ; une attendue.`,
        );
      const extracted = urlValue(attr(found[0], p.pageUrl.attribute), declared);
      if (declared && declared !== extracted)
        fail(
          "non_determine",
          "URL déclarée et URL extraite par le profil différentes.",
        );
      declared = extracted;
    }
    out.referenceUrl = declared;
    let base = declared;
    const bases = select(root, "base[href]");
    if (bases.length > 1)
      fail("non_determine", "Plusieurs bases HTML : résolution ambiguë.");
    if (bases.length === 1) base = urlValue(attr(bases[0], "href"), declared);
    const graph = new Graph(root, base);
    out.scripts = graph.scripts;
    out.partial = graph.issues.length > 0;
    for (const i of graph.issues)
      out.findings.push({
        ...issue(`json.script-${i.script}`, i.status, i.reason),
        provenance: [{ script: i.script, pointer: "", value: null }],
      });
    if (!graph.scripts)
      out.findings.push(
        issue(
          "json.absent",
          "non_determine",
          input.mode === "html_source"
            ? "Aucun JSON-LD trouvé dans le fichier source fourni ; présence après exécution de JavaScript non vérifiée."
            : "Aucun JSON-LD trouvé dans la capture DOM fournie ; moment de capture déclaré, non vérifié.",
        ),
      );
    for (const rule of p.rules) {
      const f = finding(rule);
      try {
        const scopes = select(root, rule.scope);
        if (scopes.length !== 1)
          fail(
            "non_determine",
            `${scopes.length} périmètres HTML ; un attendu.`,
          );
        const nodes = select(scopes[0], rule.selector);
        f.matchCount = nodes.length;
        if (nodes.length > 1000)
          fail("invalide", "Plus de 1 000 valeurs HTML dans une règle.");
        if (nodes.some((n) => textOf(n).length > LIMITS.valueLength))
          fail("invalide", "Valeur HTML supérieure à 100 000 caractères.");
        f.htmlExcerpt = nodes.map((n) => {
          const raw = input.html.slice(n.start, n.end);
          return raw.length > 2000
            ? raw.slice(0, 2000) +
                "… [extrait limité à 2 000 caractères ; valeurs comparées conservées ci-dessous]"
            : raw;
        });
        f.htmlRaw = nodes.map((n) => ({
          text: textOf(n),
          attribute: rule.attribute ? attr(n, rule.attribute) : null,
        }));
        if (nodes.length === 0) {
          if (rule.absence === "not_applicable" && !rule.expected) {
            let jsonAbsent = false;
            try {
              const candidate = graph.select(rule.entity, declared);
              jsonAbsent =
                graph.values(candidate, rule.property, f.provenance).length ===
                0;
            } catch (e: any) {
              if (
                e.status === "non_determine" &&
                e.message.startsWith("0 entités candidates")
              )
                jsonAbsent = true;
              else throw e;
            }
            if (!jsonAbsent)
              fail(
                "non_determine",
                "Champ HTML absent mais valeurs JSON-LD présentes ; absence optionnelle non applicable.",
              );
            f.status = "non_applicable";
            f.reason =
              rule.absenceReason! + " Absence observée des deux côtés.";
            out.findings.push(f);
            continue;
          }
          fail("non_determine", "Source HTML non trouvée.");
        }
        if (rule.cardinality === "one" && nodes.length !== 1)
          fail(
            "non_determine",
            "Sélection ambiguë : plusieurs éléments pour une valeur attendue.",
          );
        const proof = f.provenance;
        const entity = graph.select(rule.entity, declared);
        if (rule.reader === "date") {
          const json = graph.one(entity, rule.property, proof);
          f.jsonRaw = json;
          const text = space(textOf(nodes[0])),
            datetime = attr(nodes[0], "datetime");
          f.htmlRaw = {
            text,
            datetime: datetime ?? null,
            selectedAttribute: rule.attribute
              ? attr(nodes[0], rule.attribute)
              : null,
          };
          const selected =
            rule.source === "attribute"
              ? attr(nodes[0], rule.attribute!)
              : text;
          const htmlValues: {
            origin: string;
            value: ReturnType<typeof dateValue>;
          }[] = [
            {
              origin: rule.source,
              value: dateValue(selected, rule.policy.dateFormat),
            },
          ];
          if (text && rule.source === "attribute")
            htmlValues.push({
              origin: "text",
              value: dateValue(text, rule.policy.dateFormat),
            });
          if (
            datetime &&
            !(rule.source === "attribute" && rule.attribute === "datetime")
          )
            htmlValues.push({ origin: "datetime", value: dateValue(datetime) });
          const j = dateValue(json);
          f.htmlNormalized = htmlValues;
          f.jsonNormalized = j;
          const same = htmlValues.every((v) =>
            compareDates(v.value, j, p.timezone),
          );
          f.status = same ? "concordant" : "ecart";
          f.reason = same
            ? "Texte, attribut présent et JSON-LD concordent à leur précision commune."
            : "Date textuelle, attribut ou JSON-LD divergent.";
          if (
            j.precision === "day" ||
            htmlValues.some((v) => v.value.precision === "day")
          )
            f.limit += " Comparaison au jour ; heure non contrôlée.";
          out.findings.push(f);
          if (rule.property === "dateModified") {
            const c = {
              ...finding(rule, `${rule.id}.chronology`),
              label: "Ordre publication / modification",
              matchCount: f.matchCount,
              htmlExcerpt: f.htmlExcerpt,
              htmlRaw: f.htmlRaw,
              provenance: [...proof],
            };
            try {
              const pub = graph.one(entity, "datePublished", c.provenance);
              c.jsonRaw = { datePublished: pub, dateModified: json };
              c.jsonNormalized = {
                datePublished: dateValue(pub),
                dateModified: j,
              };
              c.status =
                compareOrder(j, dateValue(pub), p.timezone) < 0
                  ? "ecart"
                  : "concordant";
              c.reason =
                c.status === "ecart"
                  ? "Modification antérieure à la publication."
                  : "Ordre des deux dates balisées cohérent ; historique réel non vérifié.";
            } catch (e: any) {
              c.status = e.status || "invalide";
              c.reason = e.message;
            }
            out.findings.push(c);
          }
          if (p.referenceDate) {
            const c = {
              ...finding(rule, `${rule.id}.reference`),
              label: `${rule.label} / date de référence`,
              provenance: [...proof],
              jsonRaw: json,
              htmlRaw: p.referenceDate,
              matchCount: f.matchCount,
              htmlExcerpt: f.htmlExcerpt,
            };
            const future =
              compareOrder(j, dateValue(p.referenceDate), p.timezone) > 0;
            c.status = future ? "ecart" : "concordant";
            c.reason = future
              ? `Date après la référence explicite ${p.referenceDate}.`
              : `Date au plus égale à la référence explicite ${p.referenceDate}.`;
            c.htmlNormalized = dateValue(p.referenceDate);
            c.jsonNormalized = j;
            out.findings.push(c);
          }
          continue;
        }
        if (rule.reader === "authors") {
          const rawAuthors = graph.values(entity, rule.property, proof);
          if (!rawAuthors.length)
            fail("non_determine", "Aucun auteur balisé pour la règle.");
          f.jsonRaw = rawAuthors;
          const authors = rawAuthors.map((a) => {
            const o = graph.resolve(a);
            if (
              !graph
                .types(o)
                .some((t) => t === "Person" || t === "Organization")
            )
              fail("non_pris_en_charge", "Auteur hors Person / Organization.");
            return o;
          });
          const refs = {
            ...f,
            rule_id: `${rule.id}.references`,
            label: "Références des auteurs",
            status: "concordant" as Status,
            reason:
              "Toutes les références utilisées sont résolues dans ce fichier.",
            comparison: "Résolution locale uniquement",
            htmlNormalized: null,
            jsonNormalized: authors.map((a) => a["@id"] || "objet inline"),
          };
          out.findings.push(refs);
          const htmlNames = nodes.map((n) =>
            space(textOf(subOne(n, rule.fields!.name))),
          );
          const names = authors.map((a) =>
            space(requireString(graph.one(a, "name", proof), "author.name")),
          );
          assertUnique(htmlNames, "Noms HTML");
          assertUnique(names, "Noms JSON-LD");
          f.jsonRaw = authors.map((a) => ({
            id: a["@id"] ?? null,
            name: graph.one(a, "name"),
          }));
          f.htmlRaw = htmlNames;
          const ordered = (v: string[]) =>
            rule.policy.order === "sequence" ? v : [...v].sort();
          compare(f, ordered(htmlNames), ordered(names));
          f.rule_id = `${rule.id}.names`;
          f.label = "Noms des auteurs";
          out.findings.push(f);
          const u = {
            ...finding(rule, `${rule.id}.urls`),
            label: "Destinations des auteurs",
            matchCount: f.matchCount,
            htmlExcerpt: f.htmlExcerpt,
            provenance: [...proof],
          };
          try {
            const h = nodes.map((n, i) => ({
              name: htmlNames[i],
              url: readLinks(n, rule.fields!.url!, base),
            }));
            if (h.some((v) => !v.url))
              fail("non_determine", "URL de profil HTML absente.");
            const j = authors.map((a, i) => ({
              name: names[i],
              url: urlValue(graph.one(a, "url", u.provenance), base),
            }));
            u.htmlRaw = nodes.map((n) =>
              select(n, rule.fields!.url!).map((a) => attr(a, "href")),
            );
            u.jsonRaw = authors.map((a) => graph.values(a, "url"));
            const order = (v: any[]) =>
              rule.policy.order === "sequence"
                ? v
                : v.sort((a, b) => a.name.localeCompare(b.name, "fr"));
            compare(u, order(h), order(j));
          } catch (e: any) {
            u.status = e.status || "invalide";
            u.reason = e.message;
          }
          out.findings.push(u);
          continue;
        }
        if (rule.reader === "breadcrumb") {
          const hs = nodes.map((n) => ({
            name: space(textOf(subOne(n, rule.fields!.name))),
            url: readLinks(n, rule.fields!.url!, base),
          }));
          const raw = graph.values(entity, rule.property, proof);
          f.jsonRaw = raw;
          if (!raw.length) fail("non_determine", "Fil JSON-LD absent.");
          const js = raw
            .map((v) => {
              const o = graph.resolve(v);
              if (!graph.types(o).includes("ListItem"))
                fail("non_pris_en_charge", "ListItem attendu.");
              const position = graph.one(o, "position", proof);
              if (!Number.isInteger(position) || position < 1)
                fail("non_determine", "Position entière positive attendue.");
              const name = space(
                requireString(graph.one(o, "name", proof), "ListItem.name"),
              );
              const items = graph.values(o, "item", proof);
              if (items.length > 1)
                fail("non_determine", "Plusieurs destinations dans le fil.");
              const item = items[0];
              const rawUrl =
                item === undefined
                  ? null
                  : typeof item === "string"
                    ? item
                    : item?.["@id"];
              if (item !== undefined && typeof rawUrl !== "string")
                fail(
                  "non_pris_en_charge",
                  "item doit être une URL ou une référence @id.",
                );
              return {
                position,
                name,
                url: rawUrl === null ? null : urlValue(rawUrl, base),
              };
            })
            .sort((a, b) => a.position - b.position);
          assertUnique(
            js.map((v) => String(v.position)),
            "Positions",
          );
          if (js.some((v, i) => v.position !== i + 1))
            fail("non_determine", "Positions non contiguës dans ce profil.");
          const j = js.map(({ name, url }) => ({ name, url }));
          if (
            rule.policy.lastUnlinked === "page_url" &&
            declared &&
            hs.length &&
            j.length === hs.length
          ) {
            const last = hs.length - 1;
            if (hs[last].url === null && j[last].url === declared)
              j[last].url = null;
          }
          f.htmlRaw = nodes.map((n) => ({
            name: textOf(subOne(n, rule.fields!.name)),
            href: select(n, rule.fields!.url!).map((a) => attr(a, "href")),
          }));
          compare(f, hs, j);
        }
        if (rule.reader === "faq") {
          const hs = nodes.map((n) => {
            const question = space(textOf(subOne(n, rule.fields!.question)));
            const key =
              rule.policy.faqKey === "identifier"
                ? attr(n, rule.fields!.keyAttribute!)
                : question;
            if (!key) fail("non_determine", "Clé FAQ HTML absente.");
            return {
              key,
              question,
              answer: htmlFAQAnswer(
                subOne(n, rule.fields!.answer),
                !!rule.policy.links,
                base,
              ),
            };
          });
          const raw = graph.values(entity, rule.property, proof);
          f.jsonRaw = raw;
          if (!raw.length) fail("non_determine", "Questions JSON-LD absentes.");
          const js = raw.map((v) => {
            const q = graph.resolve(v);
            if (!graph.types(q).includes("Question"))
              fail("non_pris_en_charge", "Question attendue.");
            const question = presented(
              requireString(graph.one(q, "name", proof), "Question.name"),
            );
            const key =
              rule.policy.faqKey === "identifier"
                ? requireString(
                    graph.one(q, "identifier", proof),
                    "Question.identifier",
                  )
                : question;
            const a = graph.resolve(graph.one(q, "acceptedAnswer", proof));
            if (!graph.types(a).includes("Answer"))
              fail("non_pris_en_charge", "Answer attendue.");
            const raw = graph.one(a, "text", proof);
            return {
              key,
              question,
              answer: htmlFAQAnswer(
                htmlTree(requireString(raw, "Answer.text"), true),
                !!rule.policy.links,
                base,
              ),
            };
          });
          assertUnique(
            hs.map((x) => x.key!),
            "Questions HTML",
          );
          assertUnique(
            js.map((x) => x.key),
            "Questions JSON-LD",
          );
          const ordered = (values: any[]) =>
            values.sort((a, b) => a.key.localeCompare(b.key, "fr"));
          f.htmlRaw = hs;
          compare(f, ordered(hs), ordered(js));
        }
      } catch (e: any) {
        f.status = e instanceof ControlError ? e.status : "invalide";
        f.reason = e.message;
      }
      out.findings.push(f);
    }
  } catch (e: any) {
    out.findings.push(issue("input", e.status || "invalide", e.message));
    out.partial = true;
  }
  const expected = out.findings.filter((f) => f.expected);
  out.concordant =
    expected.length > 0 &&
    expected.every((f) => f.status === "concordant") &&
    !out.partial;
  return out;
}
export async function analyzeBatch(
  inputs: Input[],
  profile: Profile,
  onProgress?: (done: number, total: number) => void,
): Promise<Report> {
  validateProfile(profile);
  if (!inputs.length || inputs.length > LIMITS.files)
    fail("invalide", "Sélectionner entre 1 et 500 fichiers.");
  if (
    inputs.reduce((n, i) => n + new TextEncoder().encode(i.html).length, 0) >
    LIMITS.totalBytes
  )
    fail("invalide", "Volume total supérieur à 30 Mo.");
  if (new Set(inputs.map((i) => i.name)).size !== inputs.length)
    fail("invalide", "Noms de fichiers dupliqués.");
  const hash = await sha256(stable(profile));
  const pages: PageReport[] = [];
  for (const input of inputs) {
    pages.push(await analyzePage(input, profile, hash));
    onProgress?.(pages.length, inputs.length);
    await new Promise((r) => setTimeout(r, 0));
  }
  return {
    schemaVersion: VERSION.report,
    versions: VERSION,
    profile,
    profileSha256: hash,
    generatedAt: new Date().toISOString(),
    limits: LIMITS,
    pages,
    summary: {
      pages: pages.length,
      concordant: pages.filter((p) => p.concordant).length,
      differences: pages.filter((p) =>
        p.findings.some((f) => f.status === "ecart"),
      ).length,
      incomplete: pages.filter((p) =>
        p.findings.some(
          (f) =>
            f.expected &&
            ["non_determine", "non_pris_en_charge", "invalide"].includes(
              f.status,
            ),
        ),
      ).length,
      findings: pages.reduce((n, p) => n + p.findings.length, 0),
      categoriesOverlap: true,
    },
  };
}
export function csv(report: Report, delimiter = ","): string {
  if (![",", ";"].includes(delimiter))
    fail("invalide", "Séparateur CSV invalide.");
  const cell = (v: any) => {
    let s = typeof v === "string" ? v : stable(v);
    if (/^[\s\uFEFF]*[=+@\-\t\r\n]/u.test(s)) s = "'" + s;
    return '"' + s.replace(/"/g, '""') + '"';
  };
  const headers = [
    "file",
    "input_mode",
    "profile_id",
    "profile_version",
    "rule_id",
    "status",
    "reason",
    "selector",
    "match_count",
    "html_raw",
    "json_raw",
    "html_normalized",
    "json_normalized",
    "provenance",
    "comparison",
    "limit",
    "next_check",
  ];
  return (
    "\uFEFF" +
    [
      headers,
      ...report.pages.flatMap((p) =>
        p.findings.map((f) => [
          p.file,
          p.inputMode,
          p.profile.id,
          p.profile.version,
          f.rule_id,
          f.status,
          f.reason,
          f.selector,
          f.matchCount,
          f.htmlRaw,
          f.jsonRaw,
          f.htmlNormalized,
          f.jsonNormalized,
          f.provenance,
          f.comparison,
          f.limit,
          f.nextCheck,
        ]),
      ),
    ]
      .map((row) => row.map(cell).join(delimiter))
      .join("\r\n") +
    "\r\n"
  );
}
